import React, { useMemo } from "react";
import styled from "styled-components";

const ChartContainer = styled.div`
    width: 100%;
    margin-top: 1em;
    margin-bottom: 0.5em;
    padding: 0.75em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fafafa;
`;

const ChartHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75em;
    margin-bottom: 0.5em;
`;

const ChartTitle = styled.div`
    font-size: 0.9em;
    font-weight: 600;
    color: #333;
`;

const ChartSvg = styled.svg`
    display: block;
    width: 100%;
    min-height: 220px;
    height: auto;
`;

export interface BumpSeriesPoint {
    x: number;
    y: number;
}

export interface BumpSeries {
    teamId: string;
    coachName: string;
    color: string;
    points: BumpSeriesPoint[];
}

interface StandingsBumpChartProps {
    title: string;
    weekNames: string[];
    series: BumpSeries[];
}

export const StandingsBumpChart: React.FC<StandingsBumpChartProps> = ({
    title,
    weekNames,
    series
}) => {
    const chartData = useMemo(() => {
        if (weekNames.length === 0 || series.length === 0) return null;
        const marginLeft = 44;
        const marginRight = 140;
        const marginTop = 12;
        const marginBottom = 28;
        const width = 700;
        const maxRank = Math.max(...series.flatMap(s => s.points.map(p => p.y)), 1);
        const height = 280 + Math.min(series.length * 12, 120);
        const plotWidth = width - marginLeft - marginRight;
        const plotHeight = height - marginTop - marginBottom;

        const xScale = (i: number) =>
            marginLeft + (weekNames.length <= 1 ? 0 : (i / Math.max(1, weekNames.length - 1)) * plotWidth);
        // Rank 1 (best) at top of chart; rank N at bottom. SVG y increases downward so smaller y = top.
        const yScale = (rank: number) => {
            if (maxRank <= 1) return marginTop + plotHeight / 2;
            return marginTop + ((rank - 1) / (maxRank - 1)) * plotHeight;
        };

        const seriesWithPixels = series.map(s => ({
            ...s,
            points: s.points.map(p => ({ x: xScale(p.x), y: yScale(p.y) }))
        }));

        const MIN_LABEL_SPACING = 12;
        const MIN_LABEL_X_GAP = 70;
        const plotTop = marginTop;
        const plotBottom = marginTop + plotHeight;
        const seriesByLastRank = [...seriesWithPixels].sort((a, b) => {
            const aLast = a.points[a.points.length - 1];
            const bLast = b.points[b.points.length - 1];
            if (!aLast || !bLast) return 0;
            return aLast.y - bLast.y;
        });
        const placed: { x: number; y: number }[] = [];
        const labelPositions: { teamId: string; coachName: string; color: string; labelX: number; labelY: number }[] = [];
        const overlaps = (x: number, y: number) =>
            placed.some(p => Math.abs(y - p.y) < MIN_LABEL_SPACING && Math.abs(x - p.x) < MIN_LABEL_X_GAP);
        for (const s of seriesByLastRank) {
            if (s.points.length === 0) continue;
            const endPoint = s.points[s.points.length - 1];
            let labelX = endPoint.x + 6;
            let labelY = endPoint.y;
            while (overlaps(labelX, labelY)) {
                labelY += MIN_LABEL_SPACING;
            }
            labelY = Math.max(plotTop, Math.min(plotBottom, labelY));
            placed.push({ x: labelX, y: labelY });
            labelPositions.push({ teamId: s.teamId, coachName: s.coachName, color: s.color, labelX, labelY });
        }

        return {
            width,
            height,
            marginLeft,
            marginTop,
            plotWidth,
            plotHeight,
            seriesWithPixels,
            labelPositions,
            xScale,
            yScale,
            maxRank
        };
    }, [weekNames.length, series]);

    if (!chartData) return null;

    const ranks = Array.from({ length: chartData.maxRank }, (_, i) => i + 1);

    return (
        <ChartContainer>
            <ChartHeader>
                <ChartTitle>{title}</ChartTitle>
            </ChartHeader>
            <ChartSvg
                viewBox={`0 0 ${chartData.width} ${chartData.height}`}
                preserveAspectRatio="xMidYMid meet"
                aria-label="Standings rank over time"
            >
                {/* Vertical grid at each week */}
                {weekNames.map((_, i) => {
                    const x = chartData.xScale(i);
                    return (
                        <line
                            key={`v-${i}`}
                            x1={x}
                            y1={chartData.marginTop}
                            x2={x}
                            y2={chartData.marginTop + chartData.plotHeight}
                            stroke="#ddd"
                            strokeWidth={0.8}
                            strokeDasharray="3 3"
                        />
                    );
                })}
                {/* Horizontal grid at each rank */}
                {ranks.map(rank => {
                    const y = chartData.yScale(rank);
                    return (
                        <line
                            key={rank}
                            x1={chartData.marginLeft}
                            y1={y}
                            x2={chartData.marginLeft + chartData.plotWidth}
                            y2={y}
                            stroke="#ddd"
                            strokeWidth={0.8}
                            strokeDasharray="3 3"
                        />
                    );
                })}
                {/* Y-axis rank labels */}
                {ranks.map(rank => (
                    <text
                        key={rank}
                        x={chartData.marginLeft - 6}
                        y={chartData.yScale(rank) + 4}
                        textAnchor="end"
                        fontSize={10}
                        fill="#555"
                    >
                        {rank}
                    </text>
                ))}
                <text
                    x={chartData.marginLeft - 8}
                    y={chartData.marginTop + chartData.plotHeight / 2}
                    textAnchor="end"
                    fontSize={10}
                    fill="#666"
                    transform={`rotate(-90, ${chartData.marginLeft - 8}, ${chartData.marginTop + chartData.plotHeight / 2})`}
                >
                    Rank
                </text>
                {/* Team lines */}
                {chartData.seriesWithPixels.map(s => (
                    s.points.length > 0 && (
                        <polyline
                            key={s.teamId}
                            points={s.points.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    )
                ))}
                {/* Team labels at end of each line */}
                {chartData.labelPositions.map(l => (
                    <text
                        key={l.teamId}
                        x={l.labelX}
                        y={l.labelY}
                        textAnchor="start"
                        dominantBaseline="middle"
                        fontSize={9}
                        fill={l.color}
                        fontWeight={500}
                    >
                        {l.coachName}
                    </text>
                ))}
                {/* X-axis week labels */}
                {weekNames.map((name, i) => (
                    <text
                        key={name}
                        x={chartData.xScale(i)}
                        y={chartData.height - 6}
                        textAnchor="middle"
                        fontSize={9}
                        fill="#555"
                    >
                        {name.replace(/^Week\s*/i, 'W')}
                    </text>
                ))}
            </ChartSvg>
        </ChartContainer>
    );
};
